import React, { useRef, useMemo } from 'react';
import { Space, Anchor } from 'antd';
import type { RadioChangeEvent } from 'antd';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { Button, useBreadcrumbRoutes, Wrapper, Radio, DatePicker } from '@maxtropy/components';
import { useNavigate } from 'react-router-dom';
import { ReportType, REPORT_TYPE_LABELS, getDisabledDate, getDefaultDate } from './const';
import ReportOverview from './components/ReportOverview';
import EnergyTrend from './components/EnergyTrend';
import ElectricityAnalysis from './components/ElectricityAnalysis';
import WaterAnalysis from './components/WaterAnalysis';
import GasAnalysis from './components/GasAnalysis';
import styles from './index.module.scss';

const ANCHOR_ITEMS = [
  { key: 'overview', href: '#overview', title: '报告总览' },
  { key: 'trend', href: '#trend', title: '用能趋势' },
  { key: 'electricity', href: '#electricity', title: '用电分析' },
  { key: 'water', href: '#water', title: '用水分析' },
  { key: 'gas', href: '#gas', title: '用气分析' },
];

const EnergyReport: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const rightColRef = useRef<HTMLDivElement | null>(null);

  // 默认显示日报
  const [reportType, setReportType] = useState<ReportType>(ReportType.DAILY);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(getDefaultDate(ReportType.DAILY));

  // 根据报告类型过滤锚点项（日报时不显示用能趋势）
  const anchorItems = useMemo(() => {
    if (reportType === ReportType.DAILY) {
      return ANCHOR_ITEMS.filter(item => item.key !== 'trend');
    }
    return ANCHOR_ITEMS;
  }, [reportType]);

  const handleReportTypeChange = (e: RadioChangeEvent) => {
    const newType = e.target.value as ReportType;
    setReportType(newType);
    setSelectedDate(getDefaultDate(newType));
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>, link: { title: React.ReactNode; href: string }) => {
    e.preventDefault();
    const id = link.href.replace('#', '');
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const datePickerType = reportType === ReportType.DAILY ? 'date' : 'month';
  const datePickerFormat = reportType === ReportType.DAILY ? 'YYYY-MM-DD' : 'YYYY-MM';

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} className={styles.wrapper}>
      <div className={styles.top}>
        <div className={styles.title_wrapper}>
          <span className={styles.page_title}>能源报告</span>
          <Space size={16}>
            <Radio.Group optionType="button" buttonStyle="solid" value={reportType} onChange={handleReportTypeChange}>
              <Radio.Button value={ReportType.DAILY}>{REPORT_TYPE_LABELS[ReportType.DAILY]}</Radio.Button>
              <Radio.Button value={ReportType.MONTHLY}>{REPORT_TYPE_LABELS[ReportType.MONTHLY]}</Radio.Button>
            </Radio.Group>
            <DatePicker
              picker={datePickerType}
              format={datePickerFormat}
              value={selectedDate}
              onChange={date => date && setSelectedDate(date)}
              disabledDate={getDisabledDate(reportType)}
              allowClear={false}
              style={{ width: 150 }}
            />
            <Button type="primary" onClick={() => navigate('/energy/report/dayAndMonthly/config')}>
              配置
            </Button>
          </Space>
        </div>
      </div>

      <div className={styles.bottom}>
        <div className={styles.LeftColStyle}>
          <Anchor
            getContainer={() => rightColRef.current || window}
            className={styles.custom_anchor}
            onClick={handleClick}
            items={anchorItems.map(item => ({
              key: item.key,
              href: item.href,
              title: <span className={styles.label}>{item.title}</span>,
            }))}
          />
        </div>

        <div className={styles.RightColStyle} ref={rightColRef}>
          <div id="overview">
            <ReportOverview reportType={reportType} selectedDate={selectedDate} />
          </div>
          {/* 用能趋势（仅月报展示） */}
          {reportType === ReportType.MONTHLY && (
            <div id="trend">
              <EnergyTrend reportType={reportType} selectedDate={selectedDate} />
            </div>
          )}
          <div id="electricity">
            <ElectricityAnalysis reportType={reportType} selectedDate={selectedDate} />
          </div>
          <div id="water">
            <WaterAnalysis reportType={reportType} selectedDate={selectedDate} />
          </div>
          <div id="gas">
            <GasAnalysis reportType={reportType} selectedDate={selectedDate} />
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default EnergyReport;
